import { FluentProvider, Spinner, Text, webLightTheme } from '@fluentui/react-components';
import { brandOfAddress, brandForHost, type Brand, type SignInState } from '@gandhi/mail-protocol';
import { BrandProvider, BrandWordmark } from '@gandhi-group/ui';
import { Component, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import type { MailApiClient } from './api/client.js';
import { AppShell } from './components/AppShell.js';
import type { RichTextCommands } from './components/RichTextEditor.js';
import { SignIn } from './components/SignIn.js';
import { ControllerProvider, useMail } from './state/context.js';
import { activeMailbox, type MailController } from './state/mail-store.js';

export interface AppProps {
  readonly controller: MailController;
  readonly api: MailApiClient;
  readonly commands?: RichTextCommands;
  /** Off in tests that seed state directly. */
  readonly autoInit?: boolean;
}

export function App(props: AppProps): ReactElement {
  return (
    <Boundary>
      <ControllerProvider controller={props.controller}>
        <Gate {...props} />
      </ControllerProvider>
    </Boundary>
  );
}

function hostBrand(): Brand {
  return brandForHost(globalThis.location?.hostname ?? '');
}

function Splash({ label }: { label: string }): ReactElement {
  return (
    <FluentProvider theme={webLightTheme}>
      <div role="status" style={{ display: 'grid', placeItems: 'center', height: '100vh', gap: 8 }}>
        <Spinner label={label} />
      </div>
    </FluentProvider>
  );
}

/**
 * Anything that is not the mailbox and not a spinner. Branded, because a screen
 * carrying our words has to say whose they are, and because a page with no chrome
 * at all is indistinguishable from a page that failed to render.
 */
export function Notice({ title, detail }: { title: string; detail: string }): ReactElement {
  const brand = hostBrand();
  return (
    <BrandProvider brand={brand} mode="light" persistMode={false}>
      <div
        role="alert"
        style={{
          display: 'grid',
          placeContent: 'center',
          justifyItems: 'start',
          gap: 12,
          height: '100vh',
          padding: 24,
          maxWidth: 460,
          margin: '0 auto',
        }}
      >
        <BrandWordmark />
        <Text as="h1" size={500} weight="semibold">
          {title}
        </Text>
        <Text size={300}>{detail}</Text>
      </div>
    </BrandProvider>
  );
}

/**
 * The last thing between a thrown render and a white screen. React unmounts the whole
 * tree when nothing catches, and a white screen tells the person nothing at all.
 */
export class Boundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <Notice
        title="Mail could not be opened"
        detail="Something went wrong loading your mail. Reload the page, and tell us if it keeps happening."
      />
    );
  }
}

/**
 * Nothing is fetched until the mailboxes are open. The mail server checks the
 * password itself, so until someone signs in the BFF holds no credential and every
 * mail request would answer 401.
 */
function Gate(props: AppProps): ReactElement {
  const [state, setState] = useState<SignInState | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (props.autoInit === false) {
      setState({ mailboxes: [], unlocked: true });
      return;
    }
    void props.api
      .signInState()
      .then(setState)
      .catch(() => setFailed(true));
  }, [props.api, props.autoInit]);

  if (failed) {
    return (
      <Notice
        title="The mail server did not answer"
        detail="Your mail is still there. Try again in a moment."
      />
    );
  }
  if (state === undefined) return <Splash label="Opening your mailboxes…" />;

  const signOut = (): void => {
    // Locked immediately, then the server is told. Leaving the mail on screen while a
    // request is in flight is the one thing signing out must not do.
    setState({
      mailboxes: state.mailboxes.map((m) => ({ ...m, unlocked: false })),
      unlocked: false,
    });
    void props.api
      .signOut()
      .then(setState)
      .catch(() => undefined);
  };

  if (!state.unlocked) {
    // The brand is known from the address even while the mailbox is shut, so the
    // sign-in screen wears the right one rather than a neutral vendor page.
    const first = state.mailboxes[0]?.address;
    const brand = (first === undefined ? undefined : brandOfAddress(first)) ?? hostBrand();
    return (
      <BrandProvider brand={brand} mode="light" persistMode={false}>
        <SignIn api={props.api} state={state} onSignedIn={setState} />
      </BrandProvider>
    );
  }

  return <Branded {...props} onSignOut={signOut} />;
}

function Branded(props: AppProps & { readonly onSignOut: () => void }): ReactElement {
  const mailbox = useMail(activeMailbox);
  const mode = useMail((s) => s.themeMode);
  const error = useMail((s) => s.error);

  useEffect(() => {
    if (props.autoInit === false) return;
    void props.controller.init();
    props.controller.startOutboxPolling();
    return () => props.controller.stopOutboxPolling();
  }, [props.controller, props.autoInit]);

  // No brand chrome until a mailbox is known. Showing a brand we are not in, even for
  // a moment, is the exact thing the chrome exists to prevent.
  if (mailbox === null) {
    if (error !== null) return <Notice title="Your mail could not be loaded" detail={error} />;
    return <Splash label="Opening your mailboxes…" />;
  }

  return (
    <BrandProvider brand={mailbox.brandId} persistMode={false} mode={mode}>
      <AppShell
        api={props.api}
        onSignOut={props.onSignOut}
        {...(props.commands === undefined ? {} : { commands: props.commands })}
      />
    </BrandProvider>
  );
}
